import { FormGroup } from "@blueprintjs/core";
import React, { useEffect, useRef } from "react";
import {
  ChangeEventArgs,
  NumericTextBoxComponent,
} from "@syncfusion/ej2-react-inputs";
import {
  FieldLabeledProps,
  NumericFieldValue,
  NumericProps,
} from "../../types/fieldTypes";
import { RelativityTooltip } from "../../../Tooltip/RelativityTooltip";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { useOverflowTooltip } from "../../../../utils/field";

/** Base NumericTextField Props*/
export interface NumericFieldProps extends NumericProps {
  currency?: string;
  showIncrementButtons?: boolean;
  stepValue?: number;
  enableInputScrolling?: boolean;
  labelProps?: FieldLabeledProps;
  format?: string;
  isPercentage?: boolean;
}

/**Numeric Field can be used by its self or inherited for Currency Percentages etc. */
const NumericField: React.FC<NumericFieldProps> = (props) => {
  const [localValue, setLocalValue] = React.useState<
    NumericFieldValue | undefined
  >(props.value);
  const numericInputRef = useRef<NumericTextBoxComponent | null>(null);

  const isTooltipVisible = useOverflowTooltip(
    numericInputRef.current?.element,
    localValue
  );

  const preventNumberInputChangeOnWheel = (e: WheelEvent) => {
    var target = e.target as HTMLElement;
    if (!target) {
      return;
    }

    target.blur();
    e.stopPropagation();
  };

  useEffect(() => {
    if (props.enableInputScrolling !== true) {
      numericInputRef.current?.element.addEventListener(
        "wheel",
        preventNumberInputChangeOnWheel
      );
    }

    return () => {
      numericInputRef.current?.element.removeEventListener(
        "wheel",
        preventNumberInputChangeOnWheel
      );
    };
  }, [props.enableInputScrolling, numericInputRef]);

  useEffect(() => {
    setLocalValue(props.value);
  }, [props.value]);

  const format =
    props.format ??
    (props.isPercentage && (props.decimals ?? 0) > 0
      ? `0.${"0".repeat(props.decimals ?? 0)}'%'`
      : props.isPercentage
        ? "0'%'"
        : undefined);

  return (
    <div className="ucl-base-field-container ucl-base-input-numeric-field-container">
      <RelativityTooltip
        shouldShowTooltip={isTooltipVisible}
        content={!!localValue ? localValue.toString() : ""}
      >
        <FormGroup
          className={`${
            props.isRequired && !localValue && localValue !== 0
              ? "has-required-background"
              : "base-field"
          }`}
        >
          {props.label && (
            <FieldLabel
              labelName={props.label}
              tooltipDescription={props.description}
              fieldProps={{ ...props, value: localValue }}
              isRequired={props.isRequired}
              {...props.labelProps}
            />
          )}
          <NumericTextBoxComponent
            className="ucl-base-input-numeric-field"
            ref={numericInputRef}
            type={props.type}
            name={props.fieldName}
            value={localValue}
            readOnly={!!props.disabled}
            min={props?.min}
            max={props?.max}
            showSpinButton={props.showIncrementButtons}
            currency={props?.currency}
            format={format}
            validateDecimalOnType={true}
            change={(e: ChangeEventArgs) => {
              setLocalValue(e.value);
              props.onSubmit && props.onSubmit(e.value);
            }}
            decimals={props?.decimals}
            placeholder={props?.placeholder ?? "1"}
            step={props?.stepValue ?? 1}
          />
          {props.errorMessages && (
            <FormFieldErrorContainer errorMessages={props.errorMessages} />
          )}
        </FormGroup>
      </RelativityTooltip>
    </div>
  );
};

export default NumericField;
